/**
 * Types for Local Bridge MarketFit: product inputs/benefits, situation assets/needs/inputs/outputs/actors,
 * and connection links. Used by Local Bridge MarketFit API and definition UIs.
 * Core schema v2 types (Actor-Mediated Situation Model) are included for analysis-grade JSON.
 */

/** Schema version: 1 = legacy (inputs/benefits, details), 2 = core schema */
export const MARKETFIT_SCHEMA_VERSION_LEGACY = 1;
export const MARKETFIT_SCHEMA_VERSION_CORE = 2;

// Shared category for inputs, benefits, assets, needs, inputs, outputs
export const MARKETFIT_CATEGORIES = [
  'cost',
  'skill',
  'data',
  'equipment',
  'space',
  'time',
] as const;
export type MarketFitCategory = (typeof MARKETFIT_CATEGORIES)[number];

// Connection qualifier: exact (full match) or partial
export const CONNECTION_QUALIFIERS = ['exact', 'partial'] as const;
export type ConnectionQualifier = (typeof CONNECTION_QUALIFIERS)[number];

/** Maps to backend matchType: full = exact, partial = partial */
export function qualifierToMatchType(q: ConnectionQualifier): 'full' | 'partial' {
  return q === 'exact' ? 'full' : 'partial';
}
export function matchTypeToQualifier(m: 'full' | 'partial'): ConnectionQualifier {
  return m === 'full' ? 'exact' : 'partial';
}

// ----- Product (inputs_benefits_json) -----
export interface ProductInputItem {
  id: string;
  name: string;
  description?: string;
  isRequired?: boolean;
  category: MarketFitCategory;
}

export interface ProductBenefitItem {
  id: string;
  name: string;
  description?: string;
  valueProposition?: string;
  category: MarketFitCategory;
}

export interface ProductInputsBenefits {
  inputs: ProductInputItem[];
  benefits: ProductBenefitItem[];
}

// ----- Situation (details_json) -----
export interface SituationAssetItem {
  id: string;
  name: string;
  description?: string;
  cost?: number;
  isExisting?: boolean;
  category: MarketFitCategory;
}

export interface SituationNeedItem {
  id: string;
  name: string;
  description?: string;
  priority?: 'high' | 'medium' | 'low';
  category: MarketFitCategory;
}

export interface SituationInputItem {
  id: string;
  name: string;
  description?: string;
  source?: string;
  category: MarketFitCategory;
}

export interface SituationOutputItem {
  id: string;
  name: string;
  description?: string;
  value?: number;
  category: MarketFitCategory;
}

export interface SituationActorRef {
  actor_id: string;
  is_primary: boolean;
}

export interface SituationDetails {
  assets: SituationAssetItem[];
  needs: SituationNeedItem[];
  inputs: SituationInputItem[];
  outputs: SituationOutputItem[];
  actors: SituationActorRef[];
}

// ----- Deal connection_links_json -----
export interface InputAssetLink {
  id: string;
  fromId: string; // product input id
  toId: string;   // situation asset id
  matchType: 'full' | 'partial'; // full = exact, partial = partial
  productId?: string; // when deal has multiple products
  situationId?: string; // when deal has multiple situations
  userOverride?: boolean;
  notes?: string;
}

export interface BenefitNeedLink {
  id: string;
  fromId: string; // product benefit id
  toId: string;   // situation need id
  matchType: 'full' | 'partial';
  productId?: string;
  situationId?: string;
  userOverride?: boolean;
  notes?: string;
}

export interface DealConnectionLinks {
  solutionInputToSituationResourceLinks: InputAssetLink[];
  solutionBenefitToSituationNeedLinks: BenefitNeedLink[];
}

// ----- Core Schema v2 (analysis-grade) -----

export const SITUATION_VALUE_OUTPUT_TYPES = ['revenue', 'cost_avoidance', 'risk_reduction', 'capacity', 'optionality'] as const;
export const SITUATION_COST_TYPES = ['cash', 'time', 'risk', 'opportunity'] as const;
export const SITUATION_CONSTRAINT_TYPES = ['capacity', 'capital', 'skill', 'regulatory', 'time'] as const;
export const SITUATION_ASSET_TYPES = ['financial', 'physical', 'relational', 'informational', 'structural'] as const;
export const ACTOR_ROLES = ['individual', 'buyer', 'influencer', 'approver'] as const;
export const EMOTION_OPTIONS = ['anxiety', 'confidence', 'control', 'fear', 'pride', 'hope'] as const;
export const IDENTITY_DIMENSIONS = ['age', 'competence', 'status', 'autonomy'] as const;
export const INTERVENTION_EFFECTS = ['reduce', 'bypass', 'elevate'] as const;
export const ASSET_ACTIONS = ['utilize', 'unlock', 'require', 'create'] as const;
export const DELTA_DIRECTIONS = ['increase', 'decrease'] as const;
export const DURABILITY_OPTIONS = ['temporary', 'sustained'] as const;
export const COMMITMENT_SCOPES = ['capex', 'opex', 'personal'] as const;
export const TIMING_OPTIONS = ['immediate', 'delayed'] as const;

export interface SituationValueOutput {
  output_type: string;
  unit: string;
  value_rate: number;
  variability: number;
  beneficiary: string;
}

export interface SituationCost {
  cost_type: string;
  cost_rate: number;
  elasticity: 'fixed' | 'variable';
  linked_output: string;
}

export interface SituationConstraint {
  constraint_type: string;
  binding: boolean;
  severity: number;
  throughput_limit: number;
}

export interface SituationAssetV2 {
  asset_type: string;
  utilization_rate: number;
  latent_capacity: number;
}

export interface CurrencyValueRatio {
  value_per_unit_time: number;
  cost_per_unit_time: number;
}

export interface SituationSchemaV2 {
  schema_version: number;
  value_outputs: SituationValueOutput[];
  costs: SituationCost[];
  constraints: SituationConstraint[];
  assets: SituationAssetV2[];
  currency_value_ratio?: CurrencyValueRatio | null;
}

export interface DecisionAuthority {
  max_commitment: number;
  commitment_scope: string;
  reversibility_tolerance: number;
}

export interface EmotionalStateItem {
  emotion: string;
  baseline_level: number;
  sensitivity: number;
}

export interface IdentityConstraintItem {
  identity_dimension: string;
  alignment_required: boolean;
}

export interface RiskProfile {
  risk_tolerance: number;
  loss_aversion: number;
  time_discount_rate: number;
}

export interface ActorSchemaV2 {
  schema_version: number;
  decision_authority?: DecisionAuthority | null;
  emotional_state: EmotionalStateItem[];
  identity_constraints: IdentityConstraintItem[];
  risk_profile?: RiskProfile | null;
}

export interface EmotionalDeltaItem {
  emotion: string;
  direction: 'increase' | 'decrease';
  magnitude: number;
  durability: 'temporary' | 'sustained';
}

export interface EmotionalDeltaSchema {
  actor_id: string;
  source: 'situation' | 'product' | 'anticipated_outcome';
  deltas: EmotionalDeltaItem[];
}

export interface FunctionalIntervention {
  target_constraint: string;
  effect: 'reduce' | 'bypass' | 'elevate';
  magnitude: number;
}

export interface AssetInteraction {
  asset_type: string;
  action: 'utilize' | 'unlock' | 'require' | 'create';
  dependency_level: number;
}

export interface EmotionalIntervention {
  target_emotion: string;
  direction: 'increase' | 'decrease';
  mechanism: string;
  durability: 'temporary' | 'sustained';
}

export interface ProductCosts {
  price: number;
  ongoing_cost: number;
  complexity: number;
}

export interface ProductSchemaV2 {
  schema_version: number;
  functional_interventions: FunctionalIntervention[];
  asset_interactions: AssetInteraction[];
  emotional_interventions: EmotionalIntervention[];
  product_costs?: ProductCosts | null;
}

export interface EmotionalJustificationSchema {
  actor_id: string;
  emotional_value_score: number;
  emotional_cost_score: number;
  identity_alignment_score: number;
  justification_pass: boolean;
}

export interface CommitmentDecisionSchema {
  actor_id: string;
  situation_id: string;
  product_id: string;
  authorized_amount: number;
  timing: 'immediate' | 'delayed';
  reversibility: number;
  decision_pass: boolean;
}

export interface UpdatedConstraint {
  constraint_type: string;
  new_throughput_limit: number;
}

export interface UpdatedAsset {
  asset_type: string;
  utilization_change: number;
}

export interface SituationDeltaSchema {
  situation_id: string;
  updated_constraints: UpdatedConstraint[];
  updated_assets: UpdatedAsset[];
  updated_currency_value_ratio?: CurrencyValueRatio | null;
}

// Helpers to parse/serialize JSON fields
export function parseProductInputsBenefits(json: string | null | undefined): ProductInputsBenefits {
  if (!json) return { inputs: [], benefits: [] };
  try {
    const o = JSON.parse(json) as ProductInputsBenefits;
    return {
      inputs: Array.isArray(o.inputs) ? o.inputs : [],
      benefits: Array.isArray(o.benefits) ? o.benefits : [],
    };
  } catch {
    return { inputs: [], benefits: [] };
  }
}

export function parseSituationDetails(json: string | null | undefined): SituationDetails {
  if (!json) return { assets: [], needs: [], inputs: [], outputs: [], actors: [] };
  try {
    const o = JSON.parse(json) as SituationDetails;
    return {
      assets: Array.isArray(o.assets) ? o.assets : [],
      needs: Array.isArray(o.needs) ? o.needs : [],
      inputs: Array.isArray(o.inputs) ? o.inputs : [],
      outputs: Array.isArray(o.outputs) ? o.outputs : [],
      actors: Array.isArray(o.actors) ? o.actors : [],
    };
  } catch {
    return { assets: [], needs: [], inputs: [], outputs: [], actors: [] };
  }
}

export function parseDealConnectionLinks(json: string | null | undefined): DealConnectionLinks {
  if (!json) return { solutionInputToSituationResourceLinks: [], solutionBenefitToSituationNeedLinks: [] };
  try {
    const o = JSON.parse(json) as DealConnectionLinks;
    return {
      solutionInputToSituationResourceLinks: Array.isArray(o.solutionInputToSituationResourceLinks)
        ? o.solutionInputToSituationResourceLinks
        : [],
      solutionBenefitToSituationNeedLinks: Array.isArray(o.solutionBenefitToSituationNeedLinks)
        ? o.solutionBenefitToSituationNeedLinks
        : [],
    };
  } catch {
    return { solutionInputToSituationResourceLinks: [], solutionBenefitToSituationNeedLinks: [] };
  }
}

// ----- Core schema v2 parse/serialize -----
export function parseSituationSchemaV2(json: string | null | undefined): SituationSchemaV2 {
  if (!json) return defaultSituationSchemaV2();
  try {
    const o = JSON.parse(json) as SituationSchemaV2;
    return {
      schema_version: o.schema_version ?? MARKETFIT_SCHEMA_VERSION_CORE,
      value_outputs: Array.isArray(o.value_outputs) ? o.value_outputs : [],
      costs: Array.isArray(o.costs) ? o.costs : [],
      constraints: Array.isArray(o.constraints) ? o.constraints : [],
      assets: Array.isArray(o.assets) ? o.assets : [],
      currency_value_ratio: o.currency_value_ratio ?? null,
    };
  } catch {
    return defaultSituationSchemaV2();
  }
}

export function defaultSituationSchemaV2(): SituationSchemaV2 {
  return {
    schema_version: MARKETFIT_SCHEMA_VERSION_CORE,
    value_outputs: [],
    costs: [],
    constraints: [],
    assets: [],
    currency_value_ratio: null,
  };
}

export function parseActorSchemaV2(json: string | null | undefined): ActorSchemaV2 {
  if (!json) return defaultActorSchemaV2();
  try {
    const o = JSON.parse(json) as ActorSchemaV2;
    return {
      schema_version: o.schema_version ?? MARKETFIT_SCHEMA_VERSION_CORE,
      decision_authority: o.decision_authority ?? null,
      emotional_state: Array.isArray(o.emotional_state) ? o.emotional_state : [],
      identity_constraints: Array.isArray(o.identity_constraints) ? o.identity_constraints : [],
      risk_profile: o.risk_profile ?? null,
    };
  } catch {
    return defaultActorSchemaV2();
  }
}

export function defaultActorSchemaV2(): ActorSchemaV2 {
  return {
    schema_version: MARKETFIT_SCHEMA_VERSION_CORE,
    decision_authority: null,
    emotional_state: [],
    identity_constraints: [],
    risk_profile: null,
  };
}

export function parseProductSchemaV2(json: string | null | undefined): ProductSchemaV2 {
  if (!json) return defaultProductSchemaV2();
  try {
    const o = JSON.parse(json) as ProductSchemaV2;
    return {
      schema_version: o.schema_version ?? MARKETFIT_SCHEMA_VERSION_CORE,
      functional_interventions: Array.isArray(o.functional_interventions) ? o.functional_interventions : [],
      asset_interactions: Array.isArray(o.asset_interactions) ? o.asset_interactions : [],
      emotional_interventions: Array.isArray(o.emotional_interventions) ? o.emotional_interventions : [],
      product_costs: o.product_costs ?? null,
    };
  } catch {
    return defaultProductSchemaV2();
  }
}

export function defaultProductSchemaV2(): ProductSchemaV2 {
  return {
    schema_version: MARKETFIT_SCHEMA_VERSION_CORE,
    functional_interventions: [],
    asset_interactions: [],
    emotional_interventions: [],
    product_costs: null,
  };
}

export function parseEmotionalDeltaSchema(json: string | null | undefined): EmotionalDeltaSchema {
  if (!json) return { actor_id: '', source: 'situation', deltas: [] };
  try {
    const o = JSON.parse(json) as EmotionalDeltaSchema;
    return {
      actor_id: o.actor_id ?? '',
      source: o.source ?? 'situation',
      deltas: Array.isArray(o.deltas) ? o.deltas : [],
    };
  } catch {
    return { actor_id: '', source: 'situation', deltas: [] };
  }
}

export function parseSituationDeltaSchema(json: string | null | undefined): SituationDeltaSchema {
  if (!json) return { situation_id: '', updated_constraints: [], updated_assets: [], updated_currency_value_ratio: null };
  try {
    const o = JSON.parse(json) as SituationDeltaSchema;
    return {
      situation_id: o.situation_id ?? '',
      updated_constraints: Array.isArray(o.updated_constraints) ? o.updated_constraints : [],
      updated_assets: Array.isArray(o.updated_assets) ? o.updated_assets : [],
      updated_currency_value_ratio: o.updated_currency_value_ratio ?? null,
    };
  } catch {
    return { situation_id: '', updated_constraints: [], updated_assets: [], updated_currency_value_ratio: null };
  }
}

/** Detect schema version from JSON string (look for schema_version field). */
export function getMarketFitSchemaVersion(json: string | null | undefined): 1 | 2 {
  if (!json) return 1;
  try {
    const o = JSON.parse(json) as { schema_version?: number };
    return o.schema_version === 2 ? 2 : 1;
  } catch {
    return 1;
  }
}

export function generateId(): string {
  return `mf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}
